import moment from 'moment';
import i18n from '@/js/i18n';
import { convertStringToDate } from '@/js/validators/utils';

import { getProductSourceFromQueryParams } from '@/helpers/ProductSourceHelpers';

import { formatteTelephone } from '@/helpers/StringHelpers';
import { formatteCodePostal, buildAddressLine } from '@/helpers/AddressHelpers';

const getDefaultState = () => {
  return {
    nomUtilisateur: '',

    // Auth0
    mfaOption: '',
    motDePasseTemporaire: '',
    motDePasseNouveau: '',

    // Compte
    genre: '',
    courriel: '',
    prenom: '',
    nom: '',
    telephone: '',
    motDePasse: '',
    dateDeNaissance: '',
    canafe: '',
    codePromo: '',
    productSource: '',
    libellePromo: '',
    methodeChoixPromo: '',
    positionPromo: null,
    recevoirCommunication: false,
    choixAdresseValidation: '',
    validationAdresse: '',
    verificationAdresse: null,
    indRefusPropositionAdresse: false,
    limiteMaxDepot: -1,
    adresse: {
      numero: '',
      rue: '',
      ligneAdresse: '',
      appartement: '',
      ville: '',
      codePostal: '',
    },
    listeProfessions: [],
    choixProfession: {
      idSituationProfessionnelle: '',
      idProfession: '',
      secteurActivite: '',
      afficheProfession: false,
      afficheSecteur: false,
      langue: '',
    },
    lastUpdate: '',
    adresseSuggeree: false,
    contactable: false,
    channel: null,
  };
};

const state = getDefaultState();

const getters = {
  getDateDeNaissance: (state) => () => {
    return state.dateDeNaissance;
  },
  getDateOfBirth: (state) => () => {
    return moment(convertStringToDate(state.dateDeNaissance)).format('YYYY-MM-DD');
  },
  getAcquisitionSource: () => () => {
    const productSource = getProductSourceFromQueryParams();
    return productSource || 'EJ'; // Par défaut
  },
  getPhoneNumber: (state) => () => {
    return formatteTelephone(state.telephone);
  },
  getCodePostal: (state) => () => {
    return formatteCodePostal(state.adresse.codePostal);
  },
  getLastUpdate: (state) => {
    return state.lastUpdate;
  },
  getAdresseSuggeree: (state) => {
    return state.adresseSuggeree;
  },
};

const actions = {
  reset({ commit }) {
    commit('RESET');
  },
  reset_address({ commit }) {
    commit('RESET_ADDRESS');
  },
  setLastUpdate: ({ commit, state }, value) => {
    commit('SET_LASTUPDATE', value);
    return state.lastUpdate;
  },
};

const mutations = {
  RESET(state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState());
  },
  SET_LASTUPDATE: (state, value) => {
    state.lastUpdate = value;
  },

  SET_NOM_UTILISATEUR(state, value) {
    state.nomUtilisateur = value;
  },

  // Auth0
  SET_MFA_OPTION(state, value) {
    state.mfaOption = value;
  },
  SET_MOT_DE_PASSE_TEMPORAIRE(state, value) {
    state.motDePasseTemporaire = value;
  },
  SET_MOT_DE_PASSE_NOUVEAU(state, value) {
    state.motDePasseNouveau = value;
  },

  // Informations de base
  SET_GENRE(state, value) {
    state.genre = value;
  },
  SET_COURRIEL(state, value) {
    state.courriel = value;
  },
  SET_PRENOM(state, value) {
    state.prenom = value;
  },
  SET_NOM(state, value) {
    state.nom = value;
  },
  SET_TELEPHONE(state, value) {
    state.telephone = value;
  },
  SET_MOT_DE_PASSE(state, value) {
    state.motDePasse = value;
  },
  SET_DATE_DE_NAISSANCE(state, value) {
    // Convertir en date lors de la sauvegarde au store
    state.dateDeNaissance = value;
  },
  SET_RECEVOIR_COMMUNICATION(state, value) {
    state.recevoirCommunication = value;
  },
  SET_LISTE_PROFESSIONS(state, value) {
    state.listeProfessions = value;
  },
  SET_ID_PROFESSION(state, value) {
    state.choixProfession.idProfession = value;
  },
  SET_ID_SITUATION_PROFESSIONNELLE(state, value) {
    state.choixProfession.idSituationProfessionnelle = value;
  },
  SET_ID_SECTEUR_ACTIVITE(state, value) {
    state.choixProfession.secteurActivite = value;
  },
  SET_AFFICHE_PROFESSION(state, value) {
    state.choixProfession.afficheProfession = value;
  },
  SET_AFFICHE_SECTEUR(state, value) {
    state.choixProfession.afficheSecteur = value;
  },
  // TODO: Trop général pour être mis dans choixProfession
  SET_LANGUE(state, value) {
    state.choixProfession.langue = value;
  },
  // Profession/Canafe
  SET_CANAFE(state, value) {
    state.canafe = value;
  },
  // Product Source
  SET_PRODUCT_SOURCE(state, value) {
    state.productSource = value;
  },
  // Promotions
  RESET_PROMO(state) {
    state.codePromo = '';
    state.libellePromo = '';
    state.methodeChoixPromo = '';
    state.positionPromo = null;
  },
  SET_CODE_PROMO(state, value) {
    state.codePromo = value;
  },
  SET_LIBELLE_PROMO(state, value) {
    state.libellePromo = value;
  },
  SET_METHODE_CHOIX_PROMO(state, value) {
    state.methodeChoixPromo = value;
  },
  SET_POSITION_PROMO(state, value) {
    state.positionPromo = value;
  },

  // Adresse
  /**
   * Spécifications : https://lotoquebec.atlassian.net/browse/SWSPC-1652
   *
   * - les espaces en début et fin des champs doivent être "trimés/enlevés" dynamiquement sans générer une erreur
   * - les espaces multiples consécutifs ajoutés en milieu des champs doivent être dynamiquement remplacé par un seul espace
   *
   * */
  RESET_ADDRESS(state) {
    state.adresse = {
      numero: '',
      rue: '',
      ligneAdresse: '',
      appartement: '',
      ville: '',
      codePostal: '',
    };
  },
  UPDATE_ADDRESS(state, value) {
    state.adresse = {
      numero: value.numero,
      rue: value.rue,
      ligneAdresse: value.ligneAdresse || buildAddressLine(value.numero, value.rue),
      appartement: value.appartement,
      ville: value.ville,
      codePostal: value.codePostal,
    };
  },
  SET_NUMERO(state, value) {
    state.adresse.numero = value;
  },
  SET_RUE(state, value) {
    state.adresse.rue = value;
  },
  SET_LIGNE_ADRESSE(state, value) {
    state.adresse.ligneAdresse = value;
  },
  SET_APPARTEMENT(state, value) {
    state.adresse.appartement = value;
  },
  SET_VILLE(state, value) {
    state.adresse.ville = value;
  },
  SET_CODE_POSTAL(state, value) {
    state.adresse.codePostal = value;
  },
  SET_VALIDATION_ADRESSE(state, value) {
    state.validationAdresse = value;
  },
  SET_CHOIX_ADRESSE_VALIDATION(state, value) {
    state.choixAdresseValidation = value;
  },
  SET_ADRESSE_VERIFICATION_RESULT(state, value) {
    state.verificationAdresse = value;
  },
  SET_IND_REFUS_PROPOSITION_ADRESSE(state, value) {
    state.indRefusPropositionAdresse = value;
  },

  SET_LIMITE_MAX_DEPOT(state, value) {
    state.limiteMaxDepot = value;
  },

  SET_LIMITE_MAX_DEPOT_AFFICHAGE(state, value) {
    state.limiteMaxDepotAffichage = value;
  },

  SET_INFO_FROM_PLAYER_PAYMENT_PROFILE(state, playerPaymentProfile) {
    state.prenom = playerPaymentProfile.firstName;
    state.nom = playerPaymentProfile.lastName;
  },

  // Client SalesForce
  SET_USER_SF_INFO(state, data) {
    const adresse = data?.ContactAdresse;

    state.adresse.numero = adresse?.NumeroCivique ?? '';
    state.adresse.rue = adresse?.Rue ?? '';
    state.adresse.ligneAdresse = adresse ? [adresse.NumeroCivique, adresse.Rue].join(', ') : '';
    state.adresse.appartement = adresse?.NumeroAppartement ?? '';
    state.adresse.ville = adresse?.Ville ?? '';
    state.adresse.codePostal = adresse?.CodePostal ?? '';

    if (data?.Profession) {
      state.choixProfession.idProfession = data.Profession;
    }
  },

  /**
   * Spécifications : https://lotoquebec.atlassian.net/browse/SWSPC-1652
   *
   * - les espaces en début et fin des champs doivent être "trimés/enlevés" dynamiquement sans générer une erreur
   * - les espaces multiples consécutifs ajoutés en milieu des champs doivent être dynamiquement remplacé par un seul espace
   *
   * */
  SET_ADDRESS_FROM_ADDRESS_PROFILE(state, addressInfo) {
    let ligneAdresse = '';
    if (
      addressInfo.addressLine1 != '' &&
      addressInfo.addressLine2 != '' &&
      addressInfo.city != ''
    ) {
      ligneAdresse = [addressInfo.addressLine1, addressInfo.addressLine2].join(', ');
    }
    const adresse = {
      numero: addressInfo.addressLine1,
      rue: addressInfo.addressLine2,
      ligneAdresse,
      appartement: addressInfo.addressLine3,
      ville: addressInfo.city,
      codePostal: formatteCodePostal(addressInfo.postcode),
    };
    state.adresse = adresse;
  },
  SET_ADRESSESUGGEREE_FLAG(state, value) {
    state.adresseSuggeree = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
